import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LogoMark } from "@/components/ui/logo";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useWizard } from "../context/WizardContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface WizardLayoutProps {
  children: React.ReactNode;
  onSaveDraft?: () => Promise<void>;
  /** When provided and user clicks "Save & Finish" on last step, called after validation and marking step complete. Typically saves and redirects. */
  onSaveAndFinish?: () => Promise<void>;
  stepValidation?: () => string | null;
}

export function WizardLayout({ children, onSaveDraft, onSaveAndFinish, stepValidation }: WizardLayoutProps) {
  const {
    formType,
    formName,
    steps,
    currentStepIndex,
    totalSteps,
    completedSteps,
    isFirstStep,
    isLastStep,
    goNext,
    goPrev,
    goToStep,
    markStepCompleted,
  } = useWizard();

  const [saving, setSaving] = useState(false);
  const [savingFinishing, setSavingFinishing] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const currentStep = steps[currentStepIndex];
  const progressPercent = totalSteps > 0 ? ((currentStepIndex + 1) / totalSteps) * 100 : 0;

  const handleSaveDraft = async () => {
    const err = stepValidation?.();
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    try {
      if (onSaveDraft) {
        await onSaveDraft();
      }
      markStepCompleted(currentStepIndex);
      toast.success("Draft saved.");
    } catch {
      toast.error("Failed to save draft.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndContinue = async () => {
    const err = stepValidation?.();
    if (err) {
      toast.error(err);
      return;
    }
    markStepCompleted(currentStepIndex);
    if (isLastStep && onSaveAndFinish) {
      setSavingFinishing(true);
      try {
        await onSaveAndFinish();
      } catch {
        toast.error("Failed to save.");
      } finally {
        setSavingFinishing(false);
      }
      return;
    }
    goNext();
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-30 flex items-center justify-between gap-4 border-b-2 border-zinc-300 bg-white text-black px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="shrink-0" asChild aria-label="Back to my forms">
            <Link to="/file-tax">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <Link to="/file-tax" className="shrink-0 hidden sm:block" aria-label="My Professional Tax">
            <LogoMark size="sm" />
          </Link>
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open step menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0" showCloseButton={true}>
              <nav className="p-4">
                <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Steps
                </p>
                <ScrollArea className="h-[calc(100vh-8rem)]">
                  <ol className="space-y-1">
                    {steps.map((step, i) => {
                      const isCurrent = i === currentStepIndex;
                      const isDone = completedSteps.has(i);
                      const isNotDone = !isDone && i > currentStepIndex;
                      return (
                        <li key={step.id}>
                          <button
                            type="button"
                            onClick={() => {
                              goToStep(i);
                              setSidebarOpen(false);
                            }}
                            className={cn(
                              "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors",
                              isCurrent && "bg-black text-white font-semibold shadow-sm",
                              isDone && !isCurrent && "bg-success/10 text-success border border-success/30 hover:bg-success/15",
                              isNotDone && "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                              !isCurrent && !isDone && !isNotDone && "text-foreground hover:bg-accent"
                            )}
                          >
                            <span
                              className={cn(
                                "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-medium",
                                isCurrent && "border-white bg-white/20 text-white",
                                isDone && !isCurrent && "border-success bg-success text-success-foreground",
                                isNotDone && "border-muted-foreground/40 text-muted-foreground",
                                !isCurrent && !isDone && !isNotDone && "border-border"
                              )}
                            >
                              {isDone ? <Check className="h-3.5 w-3.5" /> : i + 1}
                            </span>
                            {step.title}
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                </ScrollArea>
              </nav>
            </SheetContent>
          </Sheet>
          <Badge variant="secondary" className="shrink-0">
            {formType === "BUSINESS" ? "Business" : "Individual"}
          </Badge>
          {formType === "BUSINESS" && formName ? (
            <span className="truncate text-sm text-muted-foreground" title={formName}>
              {formName}
            </span>
          ) : null}
          <span className="truncate text-sm font-medium text-foreground">
            {currentStep?.title ?? "Step"}
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleSaveDraft}
          disabled={saving}
          loading={saving}
          loadingLabel="Saving…"
        >
          Save Draft
        </Button>
      </header>

      <div className="flex flex-1">
        {/* Sidebar — desktop */}
        <aside className="hidden w-56 shrink-0 border-r border-border bg-muted/30 lg:block">
          <nav className="sticky top-[57px] p-4">
            <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Steps
            </p>
            <ol className="space-y-1">
              {steps.map((step, i) => {
                const isCurrent = i === currentStepIndex;
                const isDone = completedSteps.has(i);
                const isNotDone = !isDone && i > currentStepIndex;
                return (
                  <li key={step.id}>
                    <button
                      type="button"
                      onClick={() => goToStep(i)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors",
                        isCurrent && "bg-black text-white font-semibold shadow-sm",
                        isDone && !isCurrent && "bg-success/10 text-success border border-success/30 hover:bg-success/15",
                        isNotDone && "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                        !isCurrent && !isDone && !isNotDone && "text-foreground hover:bg-accent"
                      )}
                    >
                      <span
                        className={cn(
                          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-medium",
                          isCurrent && "border-white bg-white/20 text-white",
                          isDone && !isCurrent && "border-success bg-success text-success-foreground",
                          isNotDone && "border-muted-foreground/40 text-muted-foreground",
                          !isCurrent && !isDone && !isNotDone && "border-border"
                        )}
                      >
                        {isDone ? <Check className="h-3.5 w-3.5" /> : i + 1}
                      </span>
                      {step.title}
                    </button>
                  </li>
                );
              })}
            </ol>
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-auto focus:outline-none" tabIndex={-1} role="main">
          <div className="mx-auto max-w-3xl px-4 py-6 pb-20">
            {children}
          </div>
        </main>
      </div>

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 z-20 border-t-2 border-zinc-300 bg-white text-black px-4 py-3">
        <div className="mx-auto flex max-w-3xl flex-col gap-2">
          <Progress value={progressPercent} className="h-1" />
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              Step {currentStepIndex + 1} of {totalSteps}
            </p>
            <div className="flex gap-2">
              {!isFirstStep && (
                <Button type="button" variant="outline" onClick={goPrev}>
                  Previous
                </Button>
              )}
              <Button
                type="button"
                onClick={handleSaveAndContinue}
                disabled={savingFinishing}
                loading={savingFinishing}
                loadingLabel={isLastStep ? "Saving…" : undefined}
              >
                {isLastStep ? "Save & Finish" : "Save & Continue"}
              </Button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
